'use client';

import { Link } from '@chakra-ui/next-js';
import { Box, Button, Flex, HStack, Icon, Stack, Text } from '@chakra-ui/react';
import { getSellPath } from '@habity/services';
import { Broker, NestedHome } from '@habity/types';
import { MdCalendarMonth, MdQuestionAnswer } from 'react-icons/md';
import { ICONS } from '~/constants';
import { useAppStore } from '~/store';
import { hasBrokerCalendar } from '~/hooks/providers/useCal';
import { CalButton } from '~/components/ui/buttons/CalButton';
import { SellStepperModal } from '~/components/ui/modals/SellStepperModal';
import { useSellStepperModal } from '~/hooks/useSellStepperModal';
import { useUser } from '~/hooks/useUser';

export type BrokerCardButtonVariant =
  | 'search'
  | 'homes'
  | 'profile'
  | 'sellerDashboard';

export const BrokerCardButtons = ({
  broker,
  variant,
  home,
}: {
  broker: Broker;
  variant?: BrokerCardButtonVariant;
  home?: NestedHome;
}) => {
  const isMobile = useAppStore((state) => state.isMobile);
  const { user } = useUser();

  // Check if broker has Cal.com calendar enabled
  const hasCalendar = hasBrokerCalendar(broker);

  // Meeting modal (only used if no Cal calendar)
  const meetingModal = useSellStepperModal({
    initialBroker: broker,
    intent: 'meeting',
  });

  // Valuation modal (only used if no Cal calendar)
  const valuationModal = useSellStepperModal({
    initialBroker: broker,
    intent: 'valuation',
  });

  if (variant === 'search') {
    // For non-brokers, show 'Boka möte' if they have a calendar, plus SMS/Email
    if (!broker?.is_broker) {
      if (!hasCalendar && !broker?.email && !broker?.phone) return null;
      return (
        <HStack>
          {hasCalendar && (
            <CalButton
              broker={broker}
              intent="meeting"
              user={user}
              size="sm"
              flex="1"
              w="100%"
            >
              Boka möte
            </CalButton>
          )}
          {(broker?.email || broker?.phone) && (
            <Link
              href={
                isMobile
                  ? `sms:${broker.phone || ''}&body=Hej,%0D%0A%0D%0A`
                  : `mailto:${
                      broker.email || ''
                    }?subject=Kontakt&body=Hej,%0D%0A%0D%0A`
              }
              flex="1"
            >
              <Button
                variant={hasCalendar ? 'secondary' : undefined}
                size="sm"
                flex="1"
                w="100%"
              >
                <HStack gap={1}>
                  <Icon as={isMobile ? ICONS.SMS : ICONS.MAIL} />
                  <Text size="sm" fontWeight="medium">
                    {isMobile ? 'SMS:a' : 'Maila'}
                  </Text>
                </HStack>
              </Button>
            </Link>
          )}
        </HStack>
      );
    }

    // For actual brokers, show meeting and valuation buttons
    return (
      <>
        <HStack>
          {hasCalendar ? (
            <CalButton
              broker={broker}
              intent="meeting"
              user={user}
              size="sm"
              flex="1"
              w="100%"
            >
              Boka möte
            </CalButton>
          ) : (
            <Button size="sm" flex="1" w="100%" onClick={meetingModal.onOpen}>
              Mötesförfrågan
            </Button>
          )}

          {hasCalendar ? (
            <CalButton
              broker={broker}
              intent="valuation"
              user={user}
              variant="secondary"
              size="sm"
              flex="1"
              w="100%"
            >
              Kostnadsfri värdering
            </CalButton>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              flex="1"
              w="100%"
              onClick={valuationModal.onOpen}
            >
              Kostnadsfri värdering
            </Button>
          )}
        </HStack>

        {!hasCalendar && <SellStepperModal {...meetingModal.modalProps} />}
        {!hasCalendar && <SellStepperModal {...valuationModal.modalProps} />}
      </>
    );
  }

  if (variant === 'homes' && home)
    return (
      <>
        <Flex flexDirection="row" flexWrap="wrap" gap={2}>
          {broker.phone && (
            <Link
              href={`sms:${broker.phone}&body=Ang ${home.address.street}\n\n`}
              flex="1"
            >
              <Button
                onClick={(e) => e.stopPropagation()}
                leftIcon={<MdQuestionAnswer />}
                variant="secondary"
                size="sm"
                flex="1"
                w="100%"
              >
                Ställ en fråga
              </Button>
            </Link>
          )}
          {hasCalendar ? (
            <CalButton
              broker={broker}
              intent="meeting"
              user={user}
              leftIcon={<MdCalendarMonth />}
              variant="secondary"
              size="sm"
              flex="1"
              w="100%"
            >
              Boka möte
            </CalButton>
          ) : (
            <Button
              leftIcon={<MdCalendarMonth />}
              variant="secondary"
              size="sm"
              flex="1"
              w="100%"
              onClick={meetingModal.onOpen}
            >
              Mötesförfrågan
            </Button>
          )}
        </Flex>

        {!hasCalendar && <SellStepperModal {...meetingModal.modalProps} />}
      </>
    );

  if (variant === 'profile')
    return (
      <>
        <Box>
          {hasCalendar ? (
            <CalButton
              broker={broker}
              intent="meeting"
              user={user}
              leftIcon={<MdCalendarMonth />}
              variant="secondary"
              borderRadius="full"
              mx="auto"
              size="sm"
            >
              Boka möte
            </CalButton>
          ) : (
            <Button
              leftIcon={<MdCalendarMonth />}
              variant="secondary"
              borderRadius="full"
              mx="auto"
              size="sm"
              onClick={meetingModal.onOpen}
            >
              Mötesförfrågan
            </Button>
          )}
        </Box>

        {!hasCalendar && <SellStepperModal {...meetingModal.modalProps} />}
      </>
    );

  if (variant === 'sellerDashboard' && home) {
    return (
      <Stack alignItems="center">
        {broker.phone ? (
          <Link
            href={`sms:${broker.phone}&body=Ang ${home?.address.street}\n\n`}
          >
            <Button
              mt={2}
              colorScheme="orange"
              variant="outline"
              size="md"
              w="full"
              borderRadius="lg"
              onClick={(e) => e.stopPropagation()}
            >
              Messa {broker.name?.split(' ')[0]}
            </Button>
          </Link>
        ) : (
          <Link
            href={`mailto:${broker.email}?subject=Ang ${home?.address.street}\n\n`}
          >
            <Button
              mt={2}
              colorScheme="orange"
              variant="outline"
              size="md"
              w="full"
              borderRadius="lg"
              onClick={(e) => e.stopPropagation()}
            >
              Maila {broker.name?.split(' ')[0]}
            </Button>
          </Link>
        )}
        <Text fontSize="xs" color="gray.500">
          Få stöd och råd inför din försäljning.
        </Text>
      </Stack>
    );
  }

  return null;
};
